/**
 * ⚔️💠 SkillPicker — Reusable class-skill combat picker
 *
 * Replaces the legacy Heavy/Light 2-button picker across Arena, Wilds
 * Party Combat, and (soon) Dream Forest solo dungeon. Reads the current
 * player's hero-class kit from a configurable endpoint, shows Generator +
 * Active + Ultimate skills with mana costs / cooldown badges, and picks
 * the highest-tier affordable skill automatically after 25s.
 *
 * Props:
 *   • fightId          — active fight id (used only for identity/re-arm)
 *   • mana             — current mana bubble (0–100)
 *   • manaCap          — mana bubble cap (default 100)
 *   • skillCooldowns   — { skill_id: turn_when_free } from fight state
 *   • currentTurn      — current fight turn (for CD math)
 *   • kitEndpoint      — API path returning { skills: [...] } (default '/arena/skills')
 *   • onPick(move)     — invoked with either "heavy" / "light" (legacy fallback)
 *                        or `{ move: "skill", skill_id }` when a skill is picked.
 *                        Callers should support both shapes.
 *   • autoPickSecs     — auto-pick timer, default 25
 *   • disabled         — hard-disable all buttons
 *
 * Contract: exactly one pick per mount cycle. Caller unmounts + remounts
 * on the next turn to re-arm. This mirrors the old MovePicker contract.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🎨 Owner canon Mar 3, 2026 — skills are colored by their MIND / BODY /
 * SOUL column, not by their kind. Generator stays PURE WHITE (universal
 * gateway skill, no column identity). Ultimates keep a red accent but
 * inherit their column tint via the frame color.
 *
 * Column source order:
 *   1. Explicit `sk.column` from the backend skill spec
 *   2. Inferred from `sk.stat` (patk → body, matk → soul)
 *   3. Inferred from `sk.kind`
 *        • heal / absorb / leech / atk_m  → SOUL
 *        • atk_p                          → BODY
 *        • trigger / passive              → MIND
 */
const COLUMN_META = {
  generator: { label: "Generator", color: "#FFFFFF", frame: "border-white/70",           dot: "bg-white"           },
  mind:      { label: "Mind",      color: "#38BDF8", frame: "border-sky-400/60",         dot: "bg-sky-400"         },
  body:      { label: "Body",      color: "#F87171", frame: "border-red-500/60",         dot: "bg-red-500"         },
  soul:      { label: "Soul",      color: "#A78BFA", frame: "border-violet-500/60",      dot: "bg-violet-500"      },
  ultimate:  { label: "Ultimate",  color: "#FBBF24", frame: "border-amber-400/70",       dot: "bg-amber-400"       },
};

function inferColumn(sk) {
  const explicit = String(sk.column || "").toLowerCase();
  if (["mind", "body", "soul"].includes(explicit)) return explicit;
  const stat = String(sk.stat || "").toLowerCase();
  if (stat === "patk" || stat === "pdef") return "body";
  if (stat === "matk" || stat === "mdef") return "soul";
  const kind = String(sk.kind || "").toLowerCase();
  if (["heal", "absorb", "leech", "atk_m"].includes(kind)) return "soul";
  if (kind === "atk_p") return "body";
  if (["trigger", "passive"].includes(kind)) return "mind";
  return "body";
}

function skillKindMeta(sk) {
  if (sk.is_generator || sk.kind === "generator" ||
      ((sk.gain || 0) > 0 && (sk.cost || 0) === 0)) {
    return COLUMN_META.generator;
  }
  if (sk.bucket === "ultimates" || sk.kind === "ultimate") return COLUMN_META.ultimate;
  return COLUMN_META[inferColumn(sk)] || COLUMN_META.body;
}

export default function SkillPicker({
  fightId,
  mana = 0,
  manaCap = 100,
  skillCooldowns = {},
  currentTurn = 0,
  kitEndpoint = "/arena/skills",
  onPick,
  autoPickSecs = 25,
  disabled = false,
}) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classId, setClassId] = useState(null);
  const [armed, setArmed] = useState(true);
  const [secs, setSecs] = useState(autoPickSecs);
  const armedRef = useRef(armed);
  const skillsRef = useRef(skills);

  useEffect(() => { armedRef.current = armed; }, [armed]);
  useEffect(() => { skillsRef.current = skills; }, [skills]);

  // Fetch kit once per mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}${kitEndpoint}`);
        if (cancelled) return;
        setSkills(r.data?.skills || []);
        setClassId(r.data?.class_id || null);
      } catch {
        // Silent — legacy heavy/light fallback still works.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kitEndpoint]);

  // Timer + auto-pick
  useEffect(() => {
    if (disabled) return;
    setArmed(true);
    armedRef.current = true;
    setSecs(autoPickSecs);
    const secTick = setInterval(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    const autoPick = setTimeout(() => {
      if (!armedRef.current) return;
      armedRef.current = false;
      setArmed(false);
      // Auto-pick logic: highest-tier affordable skill.
      // 1) prefer an affordable ultimate (bigger multiplier)
      // 2) else prefer an affordable active
      // 3) else fire the generator (or legacy Light if no generator)
      const kit = skillsRef.current || [];
      const affordable = kit.filter(
        (sk) => (sk.cost || 0) <= mana && !isOnCooldown(sk.id),
      );
      const ult = affordable.find((sk) => sk.bucket === "ultimates" || sk.kind === "ultimate");
      const act = affordable.find((sk) => (sk.gain || 0) === 0 && sk.kind !== "ultimate");
      const gen = kit.find((sk) => (sk.gain || 0) > 0 && (sk.cost || 0) === 0);
      const pickSkill = ult || act || gen;
      if (pickSkill) onPick({ move: "skill", skill_id: pickSkill.id, skill: pickSkill });
      else onPick("light");   // final legacy fallback
    }, autoPickSecs * 1000);
    return () => { clearInterval(secTick); clearTimeout(autoPick); };
    // 🎯 Mar 5, 2026 — also re-arm when `currentTurn` bumps. Dream Forest
    // keeps SkillPicker mounted across turns (unlike Wilds, which unmounts
    // on mob-turn), so without this dep the picker locked after a single
    // pick → combat "freeze". Wilds/Arena continue to remount on their own
    // gates so the extra re-arm is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fightId, autoPickSecs, disabled, currentTurn]);

  const isOnCooldown = (skillId) => {
    const cdUntil = skillCooldowns?.[skillId] || 0;
    return currentTurn < cdUntil;
  };

  const cdRemaining = (skillId) => {
    const cdUntil = skillCooldowns?.[skillId] || 0;
    return Math.max(0, cdUntil - currentTurn);
  };

  const pickSkill = (sk) => {
    if (!armed || disabled) return;
    if ((sk.cost || 0) > mana) return;
    if (isOnCooldown(sk.id)) return;
    armedRef.current = false;
    setArmed(false);
    // 🎯 Mar 5, 2026 — Include the full skill object in the callback so
    // callers (e.g. Dream Forest MiniCombatModal) can read name / icon /
    // cost / gain without having to re-fetch the kit themselves.
    onPick({ move: "skill", skill_id: sk.id, skill: sk });
  };

  const pickLegacy = (m) => {
    if (!armed || disabled) return;
    armedRef.current = false;
    setArmed(false);
    onPick(m);
  };

  const manaPct = Math.round((mana / Math.max(1, manaCap)) * 100);
  const hasKit = skills.length > 0;

  // Group skills for rendering
  const groups = useMemo(() => {
    const gen = [], act = [], ult = [];
    for (const sk of skills) {
      // 🚫 Owner canon Mar 4, 2026 — passives/triggers/transcendence
      // NEVER appear in the skill picker. They're display-only on the
      // Hero Sheet. The bug: Sloane's Phantom Lag / Decoy Echo /
      // Looming Mirage / End of the Line / Shattered Reflection were
      // leaking in as clickable "actives" because the else-branch
      // caught everything the two hard checks missed.
      const skKind = String(sk.kind || "").toLowerCase();
      const skTags = Array.isArray(sk.tags) ? sk.tags.map((t) => String(t).toLowerCase()) : [];
      const isPassive = (
        skKind === "passive" || skKind === "trigger" || skKind === "transcendence" ||
        skTags.includes("passive") || skTags.includes("trigger") || skTags.includes("transcendence") ||
        sk.bucket === "passives" || sk.is_passive === true
      );
      if (isPassive) continue;

      if (sk.bucket === "ultimates" || sk.kind === "ultimate")              ult.push(sk);
      else if (sk.is_generator || sk.kind === "generator")                  gen.push(sk);
      else if ((sk.gain || 0) > 0 && (sk.cost || 0) === 0)                  gen.push(sk);
      else                                                                  act.push(sk);
    }
    // 🌱 Owner canon (Mar 2026: NO FIZZLES): every hero has a Generator.
    // The backend `/encounter/skills` route now always ships a Generator
    // for every class. This synthetic fallback covers legacy endpoints
    // (Arena, Dream Forest) that don't yet return one.
    if (gen.length === 0 && hasKit) {
      gen.push({
        id: "basic_swing", name: "Basic Swing", icon: "⚡",
        kind: "generator", cost: 0, gain: 9, is_generator: true,
        _fallback: true,
        tagline: "Reliable +9 Mana. Every hero has one until their signature Generator is wired.",
      });
    }
    return { gen, act, ult };
  }, [skills, hasKit]);

  return (
    <div
      className="bg-gradient-to-b from-black/80 to-[#0A0A0A] border-y border-[#D4AF37]/30 p-3"
      data-testid="skill-picker"
    >
      {/* Header + timer */}
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#FCD34D] font-bold">
          🎯 Your Move
        </span>
        <span
          className="text-[10px] uppercase tracking-widest text-neutral-500 tabular-nums"
          data-testid="skill-picker-timer"
        >
          Auto-pick in {secs}s
        </span>
      </div>

      {/* Mana bar */}
      <div className="mb-2" data-testid="skill-picker-mana-bar">
        <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-neutral-500 mb-1">
          <span>💠 Mana</span>
          <span className="tabular-nums text-cyan-300 font-bold">{mana} / {manaCap}</span>
        </div>
        <div className="h-1.5 bg-black border border-cyan-900/60 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-blue-400 transition-all"
            style={{ width: `${manaPct}%` }}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-[10px] text-neutral-500 italic py-2 text-center">Reading your kit…</div>
      ) : !hasKit ? (
        /* Legacy fallback — pre-class users (or classes without wired skills)
           still get 2-button combat so the fight isn't blocked. */
        <div>
          <div className="text-[10px] text-neutral-500 italic mb-2 text-center">
            {classId
              ? "No class skills wired for your hero yet — basic swings."
              : "No class picked yet — using basic swings."}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => pickLegacy("heavy")}
              disabled={!armed || disabled}
              data-testid="skill-picker-legacy-heavy"
              className="bg-black border-2 border-red-500/50 hover:border-red-400 disabled:opacity-40 py-2 px-3 flex flex-col items-center gap-1 transition"
            >
              <span className="text-xl">⚔️</span>
              <span className="text-[10px] font-bold uppercase text-red-300">Heavy</span>
            </button>
            <button
              type="button"
              onClick={() => pickLegacy("light")}
              disabled={!armed || disabled}
              data-testid="skill-picker-legacy-light"
              className="bg-black border-2 border-[#D4AF37]/50 hover:border-[#D4AF37] disabled:opacity-40 py-2 px-3 flex flex-col items-center gap-1 transition"
            >
              <span className="text-xl">💢</span>
              <span className="text-[10px] font-bold uppercase text-[#FCD34D]">Light</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.gen.length > 0 && (
            <SkillRow
              label="Generator"
              skills={groups.gen}
              armed={armed && !disabled}
              mana={mana}
              onPick={pickSkill}
              isOnCooldown={isOnCooldown}
              cdRemaining={cdRemaining}
            />
          )}
          {groups.act.length > 0 && (
            <SkillRow
              label="Actives"
              skills={groups.act}
              armed={armed && !disabled}
              mana={mana}
              onPick={pickSkill}
              isOnCooldown={isOnCooldown}
              cdRemaining={cdRemaining}
            />
          )}
          {groups.ult.length > 0 && (
            <SkillRow
              label="Ultimates"
              skills={groups.ult}
              armed={armed && !disabled}
              mana={mana}
              onPick={pickSkill}
              isOnCooldown={isOnCooldown}
              cdRemaining={cdRemaining}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SkillRow({ label, skills, armed, mana, onPick, isOnCooldown, cdRemaining }) {
  return (
    <div data-testid={`skill-row-${label.toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">
        {label}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {skills.map((sk) => {
          const meta = skillKindMeta(sk);
          const onCd = isOnCooldown(sk.id);
          const unaffordable = (sk.cost || 0) > mana;
          const disabled = !armed || onCd || unaffordable;
          const cdLeft = cdRemaining(sk.id);
          // 🎨 Column-tinted idle animation (Mar 3, 2026). Skips when
          // disabled to avoid distracting the player when the card
          // can't be used. Generator (white) + Ultimate (amber) don't
          // get an idle pulse — they have their own visual weight.
          const column = String(sk.column || "").toLowerCase();
          const idleClass = disabled
            ? ""
            : column === "mind"
            ? "col-mind-idle"
            : column === "body"
            ? "col-body-idle"
            : column === "soul"
            ? "col-soul-idle"
            : "";
          return (
            <button
              key={sk.id}
              type="button"
              onClick={() => onPick(sk)}
              disabled={disabled}
              data-testid={`skill-picker-skill-${sk.id}`}
              title={sk.tagline || sk.description || sk.name}
              className={`relative bg-black border-2 py-2 px-2 text-left transition disabled:opacity-40 hover:brightness-125 ${meta.frame} ${idleClass}`}
              style={{ boxShadow: disabled ? "none" : `0 0 12px ${meta.color}22` }}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-lg leading-none shrink-0">{sk.icon || "✨"}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-bold text-white truncate leading-tight">
                    {sk.name}
                  </div>
                  <div className="flex items-center gap-1 text-[9px] leading-none mt-0.5">
                    {(sk.cost || 0) > 0 && (
                      <span className="text-cyan-300 tabular-nums font-bold">
                        -{sk.cost}💠
                      </span>
                    )}
                    {(sk.gain || 0) > 0 && (
                      <span className="text-cyan-400 tabular-nums font-bold">
                        +{sk.gain}💠
                      </span>
                    )}
                    <span
                      className="uppercase tracking-widest text-[8px] font-bold"
                      style={{ color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </div>
                </div>
              </div>
              {onCd && (
                <span
                  className="absolute top-1 right-1 px-1 py-px rounded bg-neutral-800 text-amber-300 text-[8px] font-black tabular-nums leading-none"
                  data-testid={`skill-cd-${sk.id}`}
                >
                  {cdLeft}t
                </span>
              )}
              {unaffordable && !onCd && (
                <span className="absolute top-1 right-1 px-1 py-px rounded bg-red-950 text-red-300 text-[8px] font-black leading-none">
                  ⛔
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
